/**
 * SKU tab (epic #1643 S5): SKU unit economics absorbed from the
 * standalone `/business/supply` page (epic #997 P1.4, #1006).
 *
 * Content:
 *   - coverage summary stats (same five counters the supply page had),
 *   - the per-SKU economics table: margin / revenue / cost (all CNY —
 *     the ledger converts revenue per-row at `market_rate`, #1046) /
 *     request share / coverage status, client-side sortable on every
 *     column except coverage, with the unattributed **legacy bucket**
 *     explicitly labeled (never dropped — the window-total row includes
 *     it, so the table reconciles with the overview totals, AC 1),
 *   - the SKU x upstream coverage matrix with clickable rows,
 *   - the low-funding panel with burn-based runway in **days**
 *     (`runwayDays`, joined server-side from the #1004 upstream-runway
 *     endpoint; renders "pending" when a row's runway is indeterminate).
 *
 * Window/brand/custom bounds come from the hub's shared filter row —
 * `MetricsWindow` and `BusinessWindowPreset` share the same four
 * literals, so the hub's (narrowed) window maps 1:1.
 */
import type {
  BusinessLowFundingRunwayRow,
  BusinessSkuCoverageMatrixRow,
  BusinessSkuEconomicsResponse,
  BusinessSkuEconomicsRow,
} from '@nadoutong/contracts';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBusinessPage, type BusinessQuery } from '../../api/business.ts';
import { formatCnyCents, formatLocalTimestamp } from '../../components/formatters.ts';
import { isStale } from '../../components/freshness.ts';
import { useT, type I18nKey } from '../../i18n/index.ts';
import {
  Chip,
  KpiCard,
  Panel,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import type { MetricsTabContext } from './hubState.ts';

const INTEGER_FORMATTER = new Intl.NumberFormat('en-US');

export function SkuTab({ context }: { readonly context: MetricsTabContext }): JSX.Element {
  const query = useBusinessPage('sku-economics', businessQueryFromContext(context));
  const t = useT();
  return (
    <div className="flex min-w-0 flex-col gap-6" data-testid="metrics-sku-tab">
      {query.isLoading ? (
        <Panel caption={t('business.loading.title')} padded>
          <EmptyLine label={t('business.loading.body')} />
        </Panel>
      ) : query.isError ? (
        <div
          className="rounded-lg border border-bad-line bg-bad-dim p-5 text-body text-bad-bright"
          data-testid="business-error"
        >
          {t('business.error.prefix')}{' '}
          {query.error instanceof Error ? query.error.message : 'unknown error'}
        </div>
      ) : query.data === undefined ? (
        <Panel caption={t('business.empty.title')} padded>
          <EmptyLine label={t('business.empty.body')} />
        </Panel>
      ) : (
        <TabBody data={query.data} />
      )}
    </div>
  );
}

/** Hub context → `BusinessQuery`: same window literals, `''` brand → `'all'`. */
function businessQueryFromContext(context: MetricsTabContext): BusinessQuery {
  const custom = context.window === 'custom';
  return {
    window: context.window,
    brand: context.brand === '' ? 'all' : context.brand,
    from: custom ? context.customFrom : undefined,
    to: custom ? context.customTo : undefined,
  };
}

function TabBody({ data }: { readonly data: BusinessSkuEconomicsResponse }): JSX.Element {
  const t = useT();
  return (
    <>
      {/* Required-to-render window envelope (#1001, completed for the
          comparison period + staleness in #1008): which wall-clock period
          the figures cover, the comparison window, the timezone, and when
          they were computed (with a staleness marker). */}
      <p className="m-0 font-mono text-micro text-paper-low" data-testid="sku-econ-window">
        {t('business.sku_econ.window_label')} {formatLocalTimestamp(data.window.from)} →{' '}
        {formatLocalTimestamp(data.window.to)} ({data.window.timezone}) ·{' '}
        {t('cockpit.envelope.compare')} {formatLocalTimestamp(data.window.previousFrom)} →{' '}
        {formatLocalTimestamp(data.window.previousTo)} ·{' '}
        {t('business.status.updated')} {formatLocalTimestamp(data.generatedAt)}
        {isStale(data.generatedAt) ? (
          <span className="text-warn"> · {t('cockpit.envelope.stale')}</span>
        ) : null}
      </p>
      {data.coverage !== null ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <CompactStat label={t('business.supply.priced_skus')} value={data.coverage.pricedSkuCount} />
          <CompactStat
            label={t('business.supply.unrouted_skus')}
            value={data.coverage.unroutedSkuCount}
            tone={data.coverage.unroutedSkuCount > 0 ? 'critical' : 'normal'}
          />
          <CompactStat
            label={t('business.supply.single_point_skus')}
            value={data.coverage.singlePointSkuCount}
            tone={data.coverage.singlePointSkuCount > 0 ? 'warning' : 'normal'}
          />
          <CompactStat
            label={t('business.supply.low_funding')}
            value={data.coverage.lowFundingUpstreamCount}
            tone={data.coverage.lowFundingUpstreamCount > 0 ? 'warning' : 'normal'}
          />
          <CompactStat
            label={t('business.supply.unhealthy')}
            value={data.coverage.unhealthyUpstreamCount}
            tone={data.coverage.unhealthyUpstreamCount > 0 ? 'critical' : 'normal'}
          />
        </div>
      ) : (
        <SectionUnavailable section="coverage" />
      )}
      {data.skus !== null && data.totals !== null ? (
        <EconomicsTable rows={data.skus} totals={data.totals} />
      ) : (
        <SectionUnavailable section="skus" caption={t('business.sku_econ.table_title')} />
      )}
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.8fr)]">
        {data.matrix !== null ? (
          <CoverageMatrix rows={data.matrix} />
        ) : (
          <SectionUnavailable section="matrix" caption={t('business.supply.sku_matrix')} />
        )}
        {data.lowFunding !== null ? (
          <LowFundingPanel rows={data.lowFunding} />
        ) : (
          <SectionUnavailable section="lowFunding" caption={t('business.supply.low_funding_title')} />
        )}
      </div>
    </>
  );
}

// ─── SKU economics table ─────────────────────────────────────────────

/**
 * Client-side sort for the economics table (#1046). Every column except
 * coverage is sortable; coverage stays fixed because its status pill is
 * categorical (covered/single-point/unrouted have no total order that
 * beats the matrix panel right below).
 */
type EconSortKey = 'sku' | 'margin' | 'marginPct' | 'revenue' | 'cost' | 'requests' | 'share';

interface EconSort {
  readonly key: EconSortKey;
  readonly dir: 'asc' | 'desc';
}

/** Matches the server default (`l2_profit_cny_cents DESC`), so the
 * initial render is identical to the pre-sort behavior. */
const ECON_SORT_DEFAULT: EconSort = { key: 'margin', dir: 'desc' };

const ECON_SORT_VALUE: Record<EconSortKey, (row: BusinessSkuEconomicsRow) => number | string> = {
  sku: (row) => (row.skuName ?? row.skuId ?? '').toLowerCase(),
  margin: (row) => row.l2ProfitCnyCents,
  marginPct: (row) => row.l2MarginPct,
  revenue: (row) => row.cnyChargedCents,
  cost: (row) => row.cnyUpstreamCostCents,
  requests: (row) => row.usageCount,
  share: (row) => row.requestShare,
};

function compareEconRows(a: BusinessSkuEconomicsRow, b: BusinessSkuEconomicsRow, sort: EconSort): number {
  const va = ECON_SORT_VALUE[sort.key](a);
  const vb = ECON_SORT_VALUE[sort.key](b);
  let cmp = 0;
  if (typeof va === 'string' && typeof vb === 'string') cmp = va.localeCompare(vb);
  else if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
  if (cmp === 0) {
    // Deterministic tiebreak so equal values keep a stable order.
    cmp = (a.skuName ?? a.skuId ?? '').localeCompare(b.skuName ?? b.skuId ?? '');
  }
  return sort.dir === 'asc' ? cmp : -cmp;
}

function SortableTh({
  label,
  sortKey,
  sort,
  onSort,
  num = false,
}: {
  readonly label: string;
  readonly sortKey: EconSortKey;
  readonly sort: EconSort;
  readonly onSort: (key: EconSortKey) => void;
  readonly num?: boolean;
}): JSX.Element {
  const active = sort.key === sortKey;
  const Indicator = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <Th num={num} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cxSortButton(active)}
        data-testid={`sku-econ-sort-${sortKey}`}
      >
        {label}
        <Indicator aria-hidden className={active ? 'h-3 w-3' : 'h-3 w-3 opacity-40'} />
      </button>
    </Th>
  );
}

function cxSortButton(active: boolean): string {
  return [
    'inline-flex items-center gap-1 text-label font-medium transition-colors duration-120',
    active ? 'text-paper' : 'text-paper-low hover:text-paper',
  ].join(' ');
}

function EconomicsTable({
  rows,
  totals,
}: {
  readonly rows: readonly BusinessSkuEconomicsRow[];
  readonly totals: NonNullable<BusinessSkuEconomicsResponse['totals']>;
}): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const [sort, setSort] = useState<EconSort>(ECON_SORT_DEFAULT);
  const sortedRows = useMemo(() => [...rows].sort((a, b) => compareEconRows(a, b, sort)), [rows, sort]);
  const toggleSort = (key: EconSortKey): void => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : // Fresh numeric columns start at "biggest first"; the SKU name
          // column starts alphabetical.
          { key, dir: key === 'sku' ? 'asc' : 'desc' },
    );
  };
  return (
    <Panel caption={t('business.sku_econ.table_title')} count={rows.length} padded>
      {rows.length === 0 ? (
        <EmptyLine label={t('business.sku_econ.empty')} />
      ) : (
        <Table testId="sku-econ-table">
          <THead>
            <Tr interactive={false}>
              <SortableTh label={t('business.table.sku')} sortKey="sku" sort={sort} onSort={toggleSort} />
              <SortableTh label={t('business.sku_econ.col_margin')} sortKey="margin" sort={sort} onSort={toggleSort} num />
              <SortableTh label={t('business.sku_econ.col_margin_pct')} sortKey="marginPct" sort={sort} onSort={toggleSort} num />
              <SortableTh label={t('business.sku_econ.col_revenue')} sortKey="revenue" sort={sort} onSort={toggleSort} num />
              <SortableTh label={t('business.sku_econ.col_cost')} sortKey="cost" sort={sort} onSort={toggleSort} num />
              <SortableTh label={t('business.sku_econ.col_requests')} sortKey="requests" sort={sort} onSort={toggleSort} num />
              <SortableTh label={t('business.sku_econ.col_share')} sortKey="share" sort={sort} onSort={toggleSort} num />
              <Th>{t('business.table.coverage')}</Th>
            </Tr>
          </THead>
          <TBody>
            {sortedRows.map((row) => {
              const detailHref = row.skuId === null ? null : `/market/${encodeURIComponent(row.skuId)}`;
              return (
                <Tr
                  key={row.skuId ?? 'legacy'}
                  data-testid={`sku-econ-row-${row.skuId ?? 'legacy'}`}
                  className={detailHref === null ? undefined : 'cursor-pointer'}
                  onClick={detailHref === null ? undefined : () => navigate(detailHref)}
                >
                  <Td emphasis>
                    {row.skuId === null ? (
                      <>
                        <span className="inline-flex items-center gap-2">
                          <Chip tone="neutral">{t('business.sku_econ.legacy_bucket')}</Chip>
                        </span>
                        <div className="mt-1 text-micro font-normal text-paper-low">
                          {t('business.sku_econ.legacy_hint')}
                        </div>
                      </>
                    ) : (
                      <Link
                        to={detailHref ?? '#'}
                        className="font-mono text-paper transition-colors duration-120 hover:text-signal"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {row.skuName ?? row.skuId}
                      </Link>
                    )}
                  </Td>
                  <Td num className={row.l2ProfitCnyCents < 0 ? 'text-bad' : 'text-ok'}>
                    {formatCnyCents(row.l2ProfitCnyCents, true)}
                  </Td>
                  <Td num>{formatRatio(row.l2MarginPct)}</Td>
                  <Td num>{formatCnyCents(row.cnyChargedCents, false)}</Td>
                  <Td num>{formatCnyCents(row.cnyUpstreamCostCents, false)}</Td>
                  <Td num>{INTEGER_FORMATTER.format(row.usageCount)}</Td>
                  <Td num>{formatRatio(row.requestShare)}</Td>
                  <Td>
                    {row.coverageStatus === null ? (
                      <span className="text-paper-low">-</span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <StatusPill status={row.coverageStatus} />
                        <span className="font-mono text-micro text-paper-low">
                          {INTEGER_FORMATTER.format(row.coverage ?? 0)}
                        </span>
                      </span>
                    )}
                  </Td>
                </Tr>
              );
            })}
            {/* Window total — includes the legacy bucket, so this line
                reconciles with the overview/cockpit totals (AC 1). */}
            <Tr interactive={false} data-testid="sku-econ-totals">
              <Td emphasis>{t('business.sku_econ.totals_label')}</Td>
              <Td num className={totals.l2ProfitCnyCents < 0 ? 'text-bad' : 'text-ok'}>
                {formatCnyCents(totals.l2ProfitCnyCents, true)}
              </Td>
              <Td num>{formatRatio(totals.l2MarginPct)}</Td>
              <Td num>{formatCnyCents(totals.cnyChargedCents, false)}</Td>
              <Td num>{formatCnyCents(totals.cnyUpstreamCostCents, false)}</Td>
              <Td num>{INTEGER_FORMATTER.format(totals.usageCount)}</Td>
              <Td num>{totals.usageCount === 0 ? formatRatio(0) : formatRatio(1)}</Td>
              <Td>
                <span className="text-paper-low">-</span>
              </Td>
            </Tr>
          </TBody>
        </Table>
      )}
    </Panel>
  );
}

// ─── Coverage matrix ─────────────────────────────────────────────────

function CoverageMatrix({
  rows,
}: {
  readonly rows: readonly BusinessSkuCoverageMatrixRow[];
}): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  return (
    <Panel caption={t('business.supply.sku_matrix')} padded>
      <Table testId="sku-econ-matrix">
        <THead>
          <Tr interactive={false}>
            <Th>{t('business.table.sku')}</Th>
            <Th num>{t('business.table.coverage')}</Th>
            <Th>{t('business.table.status')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => {
            const detailHref = row.skuId === null ? null : `/market/${encodeURIComponent(row.skuId)}`;
            return (
              <Tr
                key={`${row.channel}/${row.model}/${row.protocol}`}
                data-testid={`sku-econ-matrix-row-${row.channel}-${row.model}`}
                className={detailHref === null ? undefined : 'cursor-pointer'}
                onClick={detailHref === null ? undefined : () => navigate(detailHref)}
              >
                <Td emphasis>
                  {detailHref === null ? (
                    <div className="font-mono">{row.channel}/{row.model}</div>
                  ) : (
                    <Link
                      to={detailHref}
                      className="font-mono text-paper transition-colors duration-120 hover:text-signal"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {row.channel}/{row.model}
                    </Link>
                  )}
                  <div className="font-mono text-micro font-normal text-paper-low">
                    {row.vendor} / {row.protocol}
                  </div>
                </Td>
                <Td num>{INTEGER_FORMATTER.format(row.coverage)}</Td>
                <Td>
                  <StatusPill status={row.status} />
                </Td>
              </Tr>
            );
          })}
        </TBody>
      </Table>
    </Panel>
  );
}

// ─── Low funding runway ──────────────────────────────────────────────

function LowFundingPanel({
  rows,
}: {
  readonly rows: readonly BusinessLowFundingRunwayRow[];
}): JSX.Element {
  const t = useT();
  return (
    <Panel caption={t('business.supply.low_funding_title')} padded testId="sku-econ-low-funding">
      <div className="flex flex-col gap-3">
        {rows.length === 0 ? (
          <EmptyLine label={t('business.empty.no_low_funding')} />
        ) : (
          rows.map((row) => (
            <div key={row.upstreamId} className="rounded-lg bg-well px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="m-0 truncate text-body font-medium text-paper">{row.upstreamName}</p>
                  <p className="m-0 mt-1 font-mono text-micro text-paper-low">{row.upstreamId}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p
                    className="m-0 font-mono text-dense font-semibold text-warn"
                    data-testid={`sku-econ-runway-${row.upstreamId}`}
                  >
                    {row.runwayDays === null
                      ? `- ${t('business.sku_econ.runway_pending')}`
                      : INTEGER_FORMATTER.format(Math.round(row.runwayDays))}
                  </p>
                  <p className="m-0 mt-1 text-micro text-paper-low">
                    {t('business.sku_econ.runway_days')}
                  </p>
                </div>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-raised">
                <div
                  className="h-full rounded-full bg-warn"
                  style={{ width: `${Math.max(2, Math.min(100, row.pctRemaining * 100))}%` }}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </Panel>
  );
}

// ─── Shared bits (local copies) ──────────────────────────────────────

function CompactStat({
  label,
  value,
  tone = 'normal',
}: {
  readonly label: string;
  readonly value: number;
  readonly tone?: 'normal' | 'warning' | 'critical';
}): JSX.Element {
  return (
    <KpiCard
      label={label}
      value={INTEGER_FORMATTER.format(value)}
      tone={tone === 'critical' ? 'bad' : tone === 'warning' ? 'warn' : 'default'}
      lamp={tone === 'critical' ? 'bad' : tone === 'warning' ? 'warn' : undefined}
    />
  );
}

function SectionUnavailable({
  section,
  caption,
}: {
  readonly section: string;
  readonly caption?: string;
}): JSX.Element {
  const t = useT();
  return (
    <div data-testid={`business-section-unavailable-${section}`}>
      <Panel caption={caption ?? t('business.section.unavailable')} padded>
        <EmptyLine label={t('business.section.unavailable')} />
      </Panel>
    </div>
  );
}

function EmptyLine({ label }: { readonly label: string }): JSX.Element {
  return <div className="rounded-lg bg-well px-4 py-5 text-body text-paper-low">{label}</div>;
}

function StatusPill({
  status,
}: {
  readonly status: 'covered' | 'single_point' | 'unrouted';
}): JSX.Element {
  const t = useT();
  const labelKey: I18nKey =
    status === 'covered'
      ? 'business.status.covered'
      : status === 'single_point'
        ? 'business.status.single_point'
        : 'business.status.unrouted';
  return <Chip tone={statusChipTone(status)}>{t(labelKey)}</Chip>;
}

function statusChipTone(status: 'covered' | 'single_point' | 'unrouted'): 'ok' | 'warn' | 'bad' {
  if (status === 'covered') return 'ok';
  if (status === 'single_point') return 'warn';
  return 'bad';
}

function formatRatio(value: number): string {
  if (!Number.isFinite(value)) return '-';
  return `${(value * 100).toFixed(1)}%`;
}
